import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Brain, Check, AlertCircle } from 'lucide-react';
import { agentApi, type Capabilities } from '@/api/agent';

/**
 * Mémoire (Paramètres → Mémoire), on the aikka square DA (os.css tokens, 5px
 * radii; no `rounded-full`).
 *
 * This panel is what remains of the former **Capacités**, after the pre-launch
 * pass (Notion, « Last push before going live » → Settings):
 *   • **Modèle** — deleted. The drafting-model selector asked an operator to pick
 *     a model, which is not a decision the product wants to hand over.
 *   • **Général** — deleted. « Mode d'accès aux outils » and « Recherche de
 *     connecteurs » were two controls over behaviours that are not built, so
 *     they saved a preference nothing reads.
 *   • **Mémoire** — kept, and folded into ONE block titled « Mémoire des
 *     conversations »: the two toggles now sit under that heading rather than
 *     above a separate list, because they describe that list. « Importer la
 *     mémoire » is gone — it was permanently disabled, and a button that can
 *     only ever be grey is worse than no button.
 *
 * HONEST GAP, unchanged: the two toggles persist (per-field `capabilities`
 * slice via `PUT /agent/preferences`, so one control never clobbers another),
 * but the behaviours they name are not built yet — nothing consumes them at
 * runtime. They are surfaced and saved so the choice is not lost when the
 * feature lands, and the note at the foot of the panel says exactly that. The
 * conversation list is therefore genuinely empty.
 */

const PREFERENCES_KEY = ['agent', 'preferences'] as const;
const SAVED_FLASH_MS = 2500;

const CAPABILITY_DEFAULTS: Capabilities = {
  searchReferenceConversations: true,
  generateMemory: false,
  toolAccessMode: 'auto',
  connectorSearch: true,
};

type SaveState = 'idle' | 'saving' | 'saved' | 'error';

/** Square, DA-faithful switch (no `rounded-full`). A real `role="switch"` button. */
function MemoryToggle({
  checked,
  onChange,
  disabled,
  labelId,
  testId,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
  labelId: string;
  testId: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-labelledby={labelId}
      data-testid={testId}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-control border px-0.5 outline-none transition-colors focus-visible:ring-1 focus-visible:ring-state/60 disabled:cursor-not-allowed disabled:opacity-50 ${
        checked ? 'justify-end border-signal/50 bg-signal/80' : 'justify-start border-border bg-surface-2'
      }`}
    >
      <span
        className={`h-3.5 w-3.5 rounded-control transition-colors ${
          checked ? 'bg-signal-foreground' : 'bg-ink-2'
        }`}
      />
    </button>
  );
}

function ToggleRow({
  title,
  hint,
  checked,
  onChange,
  disabled,
  id,
  testId,
}: {
  title: string;
  hint: string;
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
  id: string;
  testId: string;
}) {
  return (
    <div className="flex items-start justify-between gap-4 py-2">
      <div className="min-w-0">
        <p id={id} className="text-[0.85rem] font-medium text-foreground">
          {title}
        </p>
        <p className="text-[0.75rem] leading-snug text-ink-2">{hint}</p>
      </div>
      <MemoryToggle
        checked={checked}
        onChange={onChange}
        disabled={disabled}
        labelId={id}
        testId={testId}
      />
    </div>
  );
}

export function SettingsMemorySection() {
  const { t } = useTranslation('os');
  const queryClient = useQueryClient();

  const { data, isLoading, isError } = useQuery({
    queryKey: PREFERENCES_KEY,
    queryFn: async () => (await agentApi.getPreferences()).data,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });

  const [caps, setCaps] = useState<Capabilities>(CAPABILITY_DEFAULTS);
  const [saveState, setSaveState] = useState<SaveState>('idle');

  // Seed the local mirror once the server answers.
  useEffect(() => {
    if (data?.capabilities) setCaps({ ...CAPABILITY_DEFAULTS, ...data.capabilities });
  }, [data]);

  const disabled = isLoading || isError;

  /**
   * Persist one capability slice. Optimistic: flip the control at once, PUT only
   * the changed key, and on failure roll the control back and surface an error.
   */
  const persist = async (patch: Partial<Capabilities>) => {
    const previous = caps;
    setCaps({ ...caps, ...patch });
    setSaveState('saving');
    try {
      const res = await agentApi.updatePreferences({ capabilities: patch });
      // Keep the shared preferences cache in step so other panels see the change.
      queryClient.setQueryData(PREFERENCES_KEY, res.data);
      if (res.data?.capabilities) setCaps({ ...CAPABILITY_DEFAULTS, ...res.data.capabilities });
      setSaveState('saved');
      window.setTimeout(() => setSaveState('idle'), SAVED_FLASH_MS);
    } catch {
      setCaps(previous);
      setSaveState('error');
    }
  };

  return (
    <div className="space-y-6" data-testid="settings-memory">
      {isError && (
        <p role="alert" className="text-[0.8rem] text-destructive">
          {t('settings.memory.loadError')}
        </p>
      )}

      {/* ── Mémoire des conversations ───────────────────────────────── */}
      {/* One block, one heading: the toggles govern the list right below them,
          so they now sit under its title instead of over a second one. */}
      <section className="space-y-1" data-testid="memory-conversations">
        <div className="flex items-center gap-2">
          <Brain className="h-4 w-4 text-signal-ink" strokeWidth={1.8} aria-hidden />
          <h3 className="font-heading text-[0.95rem] font-semibold text-foreground">
            {t('settings.memory.listTitle')}
          </h3>
        </div>

        <ToggleRow
          id="memory-search-ref"
          testId="memory-toggle-searchReferenceConversations"
          title={t('settings.memory.searchRef.title')}
          hint={t('settings.memory.searchRef.hint')}
          checked={caps.searchReferenceConversations}
          disabled={disabled}
          onChange={(v) => persist({ searchReferenceConversations: v })}
        />

        <ToggleRow
          id="memory-generate"
          testId="memory-toggle-generateMemory"
          title={t('settings.memory.generate.title')}
          hint={t('settings.memory.generate.hint')}
          checked={caps.generateMemory}
          disabled={disabled}
          onChange={(v) => persist({ generateMemory: v })}
        />

        {/* Honest empty state — no memory store yet. */}
        <div className="pt-2">
          <div
            data-testid="memory-empty"
            className="rounded-control border border-dashed border-border bg-surface-2 px-3 py-4 text-center text-[0.8rem] text-ink-2"
          >
            {t('settings.memory.empty')}
          </div>
        </div>
      </section>

      {/* Honest note: persisted now, consumed once the features land. */}
      <p className="flex items-center gap-2 border-t border-border pt-3 text-[0.72rem] text-ink-2">
        <span className="flex-1">{t('settings.memory.persistNote')}</span>
        {saveState === 'saved' && (
          <span
            role="status"
            data-testid="memory-status-saved"
            className="inline-flex shrink-0 items-center gap-1 font-data text-[9px] uppercase tracking-wider text-signal-ink"
          >
            <Check className="h-3 w-3" strokeWidth={2} aria-hidden /> {t('settings.memory.saved')}
          </span>
        )}
        {saveState === 'error' && (
          <span
            role="alert"
            data-testid="memory-status-error"
            className="inline-flex shrink-0 items-center gap-1 text-[0.72rem] text-destructive"
          >
            <AlertCircle className="h-3.5 w-3.5" aria-hidden /> {t('settings.memory.saveError')}
          </span>
        )}
      </p>
    </div>
  );
}
